import * as React from "react";
import { MessageCircle, UserPlus } from "lucide-react";
import { Button } from "../../../shadcn/components/ui/Button";
import { Input } from "../../../shadcn/components/ui/Input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from "../../../shadcn/components/ui/Dialog";
import {
  Avatar,
  AvatarFallback,
} from "../../../shadcn/components/ui/Avatar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export function IndustryPopup() {
  const [open, setOpen] = React.useState(false);
  const [participants, setParticipants] = React.useState([]);
  const [groupName, setGroupName] = React.useState("");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [filteredUsers, setFilteredUsers] = React.useState([]);
  const [allUsers, setAllUsers] = React.useState([]);

  // Fetch user data on component mount
  React.useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(
          "https://silver-tlc-backend-delta.vercel.app/api/v1/alluser/get-userdata"
        );
        const data = await response.json();
        if (data.status) {
          setAllUsers(data.usersData);
        } else {
          throw new Error("Failed to fetch users");
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to fetch user data");
      }
    };

    fetchUsers();
  }, []);

  // Update filtered users based on search query
  React.useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredUsers([]);
    } else {
      const filtered = allUsers.filter((user) =>
        user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchQuery, allUsers]);

  const handleAddParticipant = (user) => {
    if (!participants.find((participant) => participant._id === user._id)) {
      setParticipants([...participants, user]);
    } else {
      toast.warning("User already added");
    }
  };

  const handleCreateChatRoom = async () => {
    const currentemail = localStorage.getItem("email");
    const token = localStorage.getItem("authToken");
  
    const payload = {
      members: [...participants.map((p) => p.email), currentemail], // Ensure current email is included
      groupName,
      token,
    };
  
    try {
      const response = await fetch(
        "https://silver-tlc-backend-delta.vercel.app/api/v1/message/chatroom/create",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
  
      if (!response.ok) {
        throw new Error("Failed to create chat room");
      }
  
      const result = await response.json();
      console.log("Chat room created:", result);
      toast.success("Chat room created successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to create chat room");
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <ToastContainer />
      <DialogTrigger asChild>
        <Button className="w-full bg-purple-600 hover:bg-purple-700 mt-3">
          Create New Chat Room
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] max-w-[330px] bg-white rounded-xl">
        <DialogHeader>
          <div className="flex items-center gap-2 font-semibold">
            <MessageCircle className="h-5 w-5" />
            Create Chat Room
          </div>
        </DialogHeader>

        <div className="space-y-4 md:pt-4 pt-0">
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none">
              Create Chat Topic
            </label>
            <Input
              placeholder="Add Company Title"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none">
                Search Users
              </label>
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or email"
              />
            </div>

            {filteredUsers.length > 0 && (
              <div className="rounded-lg border p-2 max-h-40 overflow-y-auto">
                {filteredUsers.map((user) => (
                  <div
                    key={user._id}
                    className="flex justify-between items-center p-2 hover:bg-gray-100 rounded-md cursor-pointer"
                    onClick={() => handleAddParticipant(user)}
                  >
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback>{user.name[0]}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                    </div>
                    <UserPlus className="h-5 w-5 text-gray-500" />
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-col gap-4 rounded-lg border p-4">
              {participants.map((user) => (
                <div key={user._id} className="flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback>{user.name[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{user.name}</span>
                    <span className="text-xs text-gray-500">{user.email}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateChatRoom} className="bg-purple-600 hover:bg-purple-700">
              Create
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
